import React from 'react'
import { useAppDispatch } from '../../hooks/redux'

import { getGlobalTaskStats } from '../../lib/api'
import { AddErrorAlert } from '../../slices/alert'
import { useAuth } from '../AuthProvider'
import { useEnvironment } from '../EnvironmentProvider'

type StatsContextType = {
  chatStats: {
    closed: number
    open: number
    timeseries: {
      date: string
      value: number
    }[]
  }
  chatLoaded: boolean
  taskStats: GlobalTaskStats
  taskLoaded: boolean
  globalTasks: GlobalTaskStats
  globalLoaded: boolean
}

const StatsContext = React.createContext<StatsContextType>(null)

const StatsProvider = ({
  chatStats = null,
  chatLoaded = false,
  taskStats = null,
  taskLoaded = false,
  children
}) => {
  const dispatch = useAppDispatch()

  const { isLoggedIn } = useAuth()
  const { user, loaded: envLoaded } = useEnvironment()

  const [globalTasks, setGlobalTasks] = React.useState(null)
  const [globalLoaded, setGlobalLoaded] = React.useState(false)

  const userID = React.useMemo(() => user?.id, [user])
  const canLoad = React.useMemo(
    () => isLoggedIn && envLoaded && !globalLoaded && Boolean(userID),
    [isLoggedIn, envLoaded, globalLoaded, userID]
  )

  // Fetches global task stats
  React.useEffect(() => {
    if (!canLoad) {
      return
    }

    getGlobalTaskStats()
      .then(stats => {
        setGlobalTasks(stats)
        setGlobalLoaded(true)
      })
      .catch(err => dispatch(AddErrorAlert({ err })))
  }, [canLoad, dispatch])

  return (
    <StatsContext.Provider
      value={{
        chatStats,
        chatLoaded,
        taskStats,
        taskLoaded,
        globalTasks,
        globalLoaded
      }}
    >
      {children}
    </StatsContext.Provider>
  )
}

export default StatsProvider

export const useStats = () => React.useContext(StatsContext)
